import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Swiper, Autoplay } from 'swiper';
import Plyr from 'plyr';
import SplitType from 'split-type';
import Device from '../utils/class/device';
import Popup from '../components/popup';

export default class Block01 {
    constructor (query) {
        const el = document.querySelector(query);
        if (!el) return;

        this.el = el;
        this.gsap = gsap;

        this.gsap.ticker.lagSmoothing(0);

        this.gsap.registerPlugin(ScrollTrigger);

        ScrollTrigger.normalizeScroll(true);

        const popups = document.querySelectorAll('.site-popup, .site-header--navigation');

        [...popups].forEach((popup) => {
            const observer = new MutationObserver(() => {
                const isVisible = popup.classList.contains('opened');

                ScrollTrigger.normalizeScroll(!isVisible);
            });

            observer.observe(popup, { attributes: true, attributeFilter: ['class'] });
        });

        this.init();
    }

    init () {
        this.videos();
        this.ticker();
        this.animation();
        this.parallax('.block-01--xtrnd-video .plyr', 35);
        this.popup();
    }

    videos () {
        const backVideo = this.el.querySelector('.block-01--background-video video');
        const xtrndVideo = this.el.querySelector('.block-01--xtrnd-video video');
        const previewVideo = this.el.querySelector('.block-01--preview-video video');

        if (backVideo) {
            const player = new Plyr(backVideo, { controls: [], quality: { default: 1080 } });

            player.volume = 0;

            player.play()
                .then(() => {
                    if (!player.playing) player.play();
                })
                .catch((error) => {
                    if (!player.playing) player.play();

                    console.error(error);
                });
        }

        if (xtrndVideo) {
            const player = new Plyr(xtrndVideo, { controls: [], quality: { default: 1080 } });

            player.volume = 0;

            player.play();
            
            this.gsap.timeline({
                scrollTrigger: {
                    trigger: this.el,
                    start: 'top top',
                    end: () => `+=${window.innerHeight * 7}px`,
                    onEnter: () => player.play(),
                    onLeave: () => player.pause(),
                    onEnterBack: () => player.play()
                }
            });
        }

        if (previewVideo) {
            let screens = 8;

            if (window.innerWidth < 768) screens = 9;

            const player = new Plyr(previewVideo, { controls: [], quality: { default: 1080 } });

            player.volume = 0;

            this.gsap.timeline({
                scrollTrigger: {
                    trigger: previewVideo,
                    start: `top bottom+=${window.innerHeight / 4}px`,
                    end: () => `+=${window.innerHeight * screens + window.innerHeight / 4}px`,
                    onEnter: () => player.play(),
                    onLeave: () => player.pause(),
                    onEnterBack: () => player.play(),
                    onLeaveBack: () => player.pause()
                }
            });
        }
    }

    ticker () {
        const ticker = this.el.querySelector('.block-01--ticker .swiper');
        if (!ticker) return;

        new Swiper(ticker, {
            modules: [Autoplay],
            loop: true,
            slidesPerView: 'auto',
            spaceBetween: 48,
            allowTouchMove: false,
            speed: 5000,
            autoplay: {
                delay: 0,
                disableOnInteraction: false
            }
        });
    }

    animation () {
        const els = {
            xtrnd: this.el.querySelector('[data-animation="xtrnd"]'),
            preview: this.el.querySelector('[data-animation="preview"]')
        };

        if (Object.values(els).some((el) => !el)) return;

        /* Step: Video XTRND */

        {
            const wrapper = els.xtrnd.querySelector('.block-01--xtrnd-video-wrapper');
            const heading = els.xtrnd.querySelector('.block-01--heading');
            const tag = els.xtrnd.querySelector('.block-01--tag');

            const title = heading.querySelector('.site-title');

            if (window.innerWidth > 767) {
                new SplitType(title, { types: 'chars' });
            }

            const timeline = this.gsap.timeline({
                scrollTrigger: {
                    trigger: els.xtrnd,
                    start: 'top top',
                    end: () => `+=${window.innerHeight * 7}px`,
                    pin: true,
                    pinSpacing: false,
                    scrub: true
                }
            });

            timeline
                .to(wrapper, { duration: 1, scale: 1.5, onUpdate: () => title.classList.remove('animated') })
                .to({},      { duration: 1, onUpdate: () => title.classList.add('animated') })
                .to(wrapper, { duration: 1, scale: 2.5, opacity: 0.5 })
                .to({},      { duration: 0, onUpdate: () => title.classList.remove('animated') })
                .to(wrapper, { duration: 2, scale: 5, opacity: 0 })
                .to(tag,     { duration: 1, opacity: 0 })
                .to({},      { duration: 1 });
        }

        /* Step: Preview Video */

        {
            let width = 'calc(100dvw - 180px)';
            let height = 'calc(100dvh - 120px)';

            if (window.innerWidth < 768) {
                width = 'calc(100dvw - 40px)';
                height = 'calc(100dvh - 120px)';
            }   

            const wrapper = els.preview.querySelector('.block-01--preview-video-wrapper');

            const timeline = this.gsap.timeline({
                scrollTrigger: {
                    trigger: els.preview,
                    start: 'top top',
                    end: () => `+=${window.innerHeight * 7}px`,
                    pin: true,
                    pinSpacing: false,
                    scrub: true
                }
            });

            timeline
                .to({},      { duration: 2 })
                .to(wrapper, { duration: 2, width, height })
                .to(wrapper, { duration: 2, width: '100dvw', height: '100dvh', borderRadius: 0 })
                .to({},      { duration: 1 });
        }

        /* Step: Preview Text */

        {
            const intro = els.preview.querySelector('.block-01--heading.intro .site-title.desktop');
            const introMobile = els.preview.querySelector('.block-01--heading.intro .site-title.mobile');
            const outro = els.preview.querySelector('.block-01--heading.outro .site-title.desktop');
            const outroMobile = els.preview.querySelector('.block-01--heading.outro .site-title.mobile');
            const button = els.preview.querySelector('.block-01--heading.button');

            new SplitType(intro, { types: 'chars' });
            new SplitType(introMobile, { types: 'chars' });
            new SplitType(outro, { types: 'chars' });
            new SplitType(outroMobile, { types: 'chars' });

            const timeline = this.gsap.timeline({
                scrollTrigger: {
                    trigger: els.preview,
                    start: 'top bottom',
                    end: () => `+=${window.innerHeight * 8}px`,
                    scrub: true
                }
            });

            timeline
                .to({}, {
                    duration: 0.75,
                    onUpdate: () => {
                        intro.classList.remove('animated');
                        introMobile.classList.remove('animated');
                    }
                })
                .to({}, {
                    duration: 2.25,
                    onUpdate: () => {
                        intro.classList.add('animated');
                        introMobile.classList.add('animated');

                        outro.classList.remove('animated');
                        outroMobile.classList.remove('animated');
                    }
                })
                .to({}, {
                    duration: 1,
                    onUpdate: () => {
                        intro.classList.remove('animated');
                        introMobile.classList.remove('animated');

                        outro.classList.add('animated');
                        outroMobile.classList.add('animated');
                    }
                })
                .to({}, { duration: 2, onUpdate: () => button.classList.remove('animated') })
                .to({}, { duration: 1, onUpdate: () => button.classList.add('animated') })
                .to({}, { duration: 1 });
        }
    }

    parallax (query, power) {
        const element = this.el.querySelector(query);
        if (!element) return;

        const { isMac, isSafari } = Device;

        if (isMac && isSafari) return;

        let frameId;
        let mouseX, mouseY = 0;

        document.addEventListener('mousemove', (event) => {
            mouseX = event.clientX;
            mouseY = event.clientY;

            if (window.innerWidth < 1200 || (window.scrollY > window.innerHeight * 7)) return;

            if (!frameId) {
                frameId = requestAnimationFrame(() => {
                    const rect = element.getBoundingClientRect();
                    const x = mouseX - rect.left;
                    const y = mouseY - rect.top;
                
                    const centerX = rect.width / 2;
                    const centerY = rect.height / 2;
                
                    const moveX = (x - centerX) / centerX;
                    const moveY = (y - centerY) / centerY;
                
                    const translateX = moveX * -power;
                    const translateY = moveY * -power;
                
                    element.style.transform = `translate(${translateX}px, ${translateY}px)`;

                    frameId = null;
                });
            }
        });
    }

    popup () {
        const trigger = this.el.querySelector('.site-popup-trigger[data-popup="preview-popup"]');
        if (!trigger) return;

        const popup = this.el.querySelector('.site-popup[data-popup="preview-popup"]');
        if (!popup) return;

        const window = popup.querySelector('.site-popup-window[data-popup="preview-popup"]');

        const video = popup.querySelector('.block-01--popup-video');
        if (!video) return;

        const player = new Plyr(video, {
            quality: { default: 1080 },
            youtube: {
                noCookie: true,
                rel: 0,
                showinfo: 0,
                modestbranding: 1,
                iv_load_policy: 3,
                disablekb: 1
            }
        });

        new Popup({ popup, toggle: trigger, window }, {
            open: () => {
                player.muted = false;
                player.volume = 0.5;

                player.play();
            },
            close: () => player.stop()
        });
    }
}
